/**
 * Menu configuration for NavigationBar component
 * Converted from Angular hamburger-menu menus.ts
 */

// Import config to access environment variables
import { ROUTES_PATHS } from '@src/constants/routes';
import { config } from '@uhg-abyss/web/tools/config';
import { NETWORK_MANAGEMENT_ALLOWED_ROLES, MENU_KEYS } from './constant';
// Declare global functions available in browser environment
declare const atob: (data: string) => string;
import {
  MENU_KEYS as psmsMenu,
  SESSION_KEYS,
  PEM_FEATURE_ENABLED,
  CRO_FEATURE_ENABLED,
} from '@src/constants/featureFlags';

// Views enum (matches hamburger-menu)
export const Views = {
  General: 'Provider Performance',
  GeoSegmentation: 'Geographic Segmentation',
  ScoreCard: 'Contract Intelligence',
  MedicalCost: 'Affordability Drivers',
  Dashboards: 'Operations',
  ProviderBriefing: 'Provider Negotiation Briefing',
  ProviderExperience: 'Provider Experience',
};

export const Logins = { Internal: 'Internal', External: 'External' };
export const Modes = { Simulation: 'Simulation', General: 'General' };

// Helper function to get roles from Angular (dispatched via MenuRolesUpdated event or sessionStorage)
// This replaces the individual role check functions
export const getMenuRoles = (): {
  admRole: boolean;
  scoreCardRole: boolean;
  pemRole: boolean;
  pcorData: boolean;
  admData: boolean;
  affData: boolean;
  cesfData: boolean;
  pocaEnabled: boolean; // VIM status
} => {
  try {
    const rolesStr = sessionStorage.getItem('menuRoles');
    const roles = rolesStr
      ? JSON.parse(rolesStr)
      : {
          admRole: false,
          scoreCardRole: false,
          pemRole: false,
          pcorData: false,
          admData: false,
          affData: false,
          cesfData: false,
          pocaEnabled: false,
        };

    const affStr = sessionStorage.getItem('aff');
    if (affStr) {
      const affObj = JSON.parse(affStr);
      if (affObj && affObj.Triggered === true) {
        roles.affData = affObj.Active === true;
      }
    }

    const admStr = sessionStorage.getItem('adm');
    if (admStr) {
      const admObj = JSON.parse(admStr);
      if (admObj && admObj.Triggered === true) {
        roles.admData = admObj.Active === true;
      }
    }

    return roles;
  } catch (_e) {
    console.error('Error getting menu roles:', _e);
    return {
      admRole: false,
      scoreCardRole: false,
      pemRole: false,
      pcorData: false,
      admData: false,
      affData: false,
      cesfData: false,
      pocaEnabled: false,
    };
  }
};

// Helper function to check if user is Internal Admin
export const isInternalAdmin = (): boolean => {
  try {
    const currentUserStr = sessionStorage.getItem('currentUser');
    if (currentUserStr) {
      const currentUser = JSON.parse(currentUserStr);
      if (currentUser && currentUser[0]) {
        // Check if user has UHCI_Admin role
        interface UserPersona {
          UserRole: string;
        }
        return currentUser[0].UserPersonas?.some(
          (item: UserPersona) => item.UserRole === 'UHCI_Admin'
        );
      }
    }
    return false;
  } catch (_e) {
    console.error('Error checking isInternalAdmin:', _e);
    return false;
  }
};

// Helper function to check if user can access Network Management
export const hasNetworkManagementAccess = (): boolean => {
  try {
    const currentUserStr = sessionStorage.getItem('currentUser');
    if (currentUserStr) {
      const currentUser = JSON.parse(currentUserStr);
      if (currentUser && currentUser[0]) {
        interface UserPersona {
          UserRole: string;
        }
        return (
          currentUser[0].UserPersonas?.some((item: UserPersona) =>
            NETWORK_MANAGEMENT_ALLOWED_ROLES.includes(item.UserRole)
          ) || false
        );
      }
    }
    return false;
  } catch (_e) {
    return false;
  }
};

// Helper function to check if external user has multiple providers
// Matches Angular logic: hamburger-menu.component.ts lines 1715-1718
export const getExternalProvidersCount = (): boolean => {
  try {
    const enableTinLevelExpirience = sessionStorage.getItem('enableTinLevelExperience') === 'true';

    if (enableTinLevelExpirience) {
      // Check mpinTinData for TIN level experience
      const mpinDataStr = sessionStorage.getItem('mpinTinData');
      if (mpinDataStr) {
        const mpinData = JSON.parse(mpinDataStr);
        return mpinData?.length > 1;
      }
    } else {
      // Check currentUser Providers
      const currentUserStr = sessionStorage.getItem('currentUser');
      if (currentUserStr) {
        const currentUser = JSON.parse(currentUserStr);
        if (currentUser && currentUser[0]) {
          return currentUser[0].Providers && currentUser[0].Providers.length > 1;
        }
      }
    }
    return false;
  } catch (e) {
    console.error('Error checking getExternalProvidersCount:', e);
    return false;
  }
};

// Helper function to check if Impact button should be shown at all
// Hidden for multi-TIN (>1 TIN selected), geo segmentation and HiVolume flows
// When visible, Impact is always enabled — visibility is the sole gate
export const shouldShowImpact = (): boolean => {
  try {
    if (sessionStorage.getItem('HIVOLUMETINFLOW') === 'true') return false;
    const multiTinNavigation = sessionStorage.getItem('multiTinNavigation');
    const multiSelectedTins = sessionStorage.getItem('multiSelectedTins');
    if (multiTinNavigation === 'true' && multiSelectedTins) {
      const tinCount = multiSelectedTins.split(',').length;
      if (tinCount > 1) return false;
    }
    return true;
  } catch (_e) {
    return false;
  }
};

export interface MenuItem {
  key: string;
  name: string;
  path: string;
  disabled: boolean;
  hasDropdown?: boolean;
  children?: SubMenuItem[];
  access?: string[][]; // Access control like hamburger-menu: [Login, View, Mode]
}

export interface SubMenuItem {
  key: string;
  name: string;
  path: string;
  disabled: boolean;
  children?: SubMenuItem[];
}

// Check if High Volume TIN flow is active
const isHiVolumePage = sessionStorage.getItem('HIVOLUMETINFLOW') === 'true' ? true : false;
// Check if Multi-TIN navigation flow is active
const isMultiTinPage = sessionStorage.getItem('multiTinNavigation') === 'true' ? true : false;
const isSingleExtendedTinSelected = sessionStorage.getItem('psmsActiveChipOption') ? true : false;

// First Row Navigation Menus (Primary Navigation)
export const NavigationMenus: MenuItem[] = [
  {
    key: 'dashboard',
    name: 'My Home',
    path: '/myhome',
    disabled: false,
    hasDropdown: false,
    access: [
      [Logins.Internal, Views.General, Modes.General],
      [Logins.Internal, Views.GeoSegmentation, Modes.General],
      [Logins.Internal, Views.ScoreCard, Modes.General],
      [Logins.Internal, Views.MedicalCost, Modes.General],
      [Logins.Internal, Views.Dashboards, Modes.General],
      [Logins.Internal, Views.ProviderBriefing, Modes.General],
      [Logins.Internal, Views.ProviderExperience, Modes.General],
    ],
  },
  {
    key: 'myInsights',
    name: 'My Insights',
    path: ROUTES_PATHS.MyInsights,
    disabled: false,
    hasDropdown: false,
    access: [
      [Logins.Internal, Views.General, Modes.Simulation],
      [Logins.External, Views.General, Modes.General],
    ],
  },
  {
    key: 'overview',
    name: 'Overview',
    path: ROUTES_PATHS.Overview.OverviewAdvocate,
    disabled: false,
    hasDropdown: false,
    access: [[Logins.Internal, Views.General, Modes.General]],
  },
  {
    key: 'reimbursements',
    name: 'Reimbursements',
    path: ROUTES_PATHS.Reimbursements.Processed,
    disabled: false,
    hasDropdown: true,
    access: [
      [Logins.Internal, Views.General, Modes.General],
      [Logins.Internal, Views.GeoSegmentation, Modes.General],
    ],
    children: [
      {
        key: 'processed',
        name: 'Processed',
        path: ROUTES_PATHS.Reimbursements.Processed,
        disabled: false,
      },
      {
        key: 'payments',
        name: 'Payments',
        path: ROUTES_PATHS.Reimbursements.Payments,
        disabled: false,
      },
      {
        key: 'nonpayments',
        name: 'Non-Payments',
        path: ROUTES_PATHS.Reimbursements.NonPayments,
        disabled: false,
      },
      {
        key: 'reconsiderations',
        name: 'Reconsiderations',
        path: ROUTES_PATHS.Reimbursements.Reconsiderations,
        disabled: false,
      },
      {
        key: 'overpayments',
        name: 'Overpayments',
        path: ROUTES_PATHS.Reimbursements.OverPayments,
        disabled: false,
      },
      {
        key: 'appeals',
        name: 'Appeals',
        path: ROUTES_PATHS.Reimbursements.Appeals,
        disabled: false,
      },
      {
        key: 'paymentIntegrity',
        name: 'Payment Integrity',
        path: '/Reimbursements/PaymentIntegrity',
        disabled: isHiVolumePage ? true : false,
        children: [
          {
            key: 'medicalRecordEvaluations',
            name: 'Medical Record Evaluations',
            path: ROUTES_PATHS.Reimbursements.PaymentIntegrity,
            disabled: false,
          },
        ],
      },
      {
        key: 'settlements',
        name: 'Settlements',
        path: '/reimbursements/settlements',
        disabled: isHiVolumePage ? true : false,
      },
    ],
  },
  {
    key: 'networkmanagement',
    name: 'Network Management',
    path: '/network-management',
    disabled: false,
    hasDropdown: true,
    access: [
      [Logins.Internal, Views.General, Modes.General],
      [Logins.Internal, Views.GeoSegmentation, Modes.General],
    ],
    children: [
      {
        key: 'exemption',
        name: 'Exemption Summary',
        path: ROUTES_PATHS.NetworkManagement.ExemptSummary,
        disabled: false,
      },
      {
        key: 'affordability',
        name: 'Affordability Programs',
        path: ROUTES_PATHS.NetworkManagement.AffSummary,
        disabled: false,
      },
      {
        key: 'adm',
        name: 'Appropriate Decision Monitoring (ADM)',
        path: ROUTES_PATHS.NetworkManagement.ADMReport,
        disabled: false,
      },
      {
        key: 'vbc',
        name: 'Value Based Contracting',
        path: ROUTES_PATHS.NetworkManagement.VBC,
        disabled: false,
      },
      {
        key: 'cro',
        name: 'Contract Renewal & Remediation',
        path: ROUTES_PATHS.NetworkManagement.ContractRemidiation,
        disabled: false,
      },
      {
        key: 'redirections',
        name: 'Site of Service Redirections',
        path: ROUTES_PATHS.NetworkManagement.Redirections,
        disabled: false,
      },
    ],
  },
  {
    key: 'priorauth',
    name: 'Prior Authorizations',
    path: ROUTES_PATHS.PriorAuth.Summary,
    disabled: false,
    hasDropdown: true,
    access: [
      [Logins.Internal, Views.General, Modes.General],
      [Logins.Internal, Views.GeoSegmentation, Modes.General],
      [Logins.Internal, Views.General, Modes.Simulation],
      [Logins.External, Views.General, Modes.General],
    ],
    children: [
      {
        key: 'passummary',
        name: 'Summary',
        path: ROUTES_PATHS.PriorAuth.Summary,
        disabled: false,
      },
      {
        key: 'goldCard',
        name: 'Exemption Programs',
        path: ROUTES_PATHS.PriorAuth.GoldCard,
        disabled: false,
      },
    ],
  },
  {
    key: 'pcor',
    name: 'Patient Care Opportunity',
    path: ROUTES_PATHS.PatientCROpportunity,
    disabled: true, // Enabled dynamically based on PCOR data availability
    hasDropdown: false,
    access: [
      [Logins.Internal, Views.General, Modes.General],
      [Logins.Internal, Views.General, Modes.Simulation],
      [Logins.External, Views.General, Modes.General],
    ],
  },
  {
    key: 'serviceinteraction',
    name: 'Service Interaction',
    path: '/service-interaction',
    disabled: false,
    hasDropdown: true,
    access: [
      [Logins.Internal, Views.General, Modes.General],
      [Logins.Internal, Views.GeoSegmentation, Modes.General],
      [Logins.Internal, Views.General, Modes.Simulation],
      [Logins.External, Views.General, Modes.General],
    ],
    children: [
      {
        key: 'calls',
        name: 'Calls',
        path: '/service-interaction/calls',
        disabled: false,
      },
      {
        key: 'digitalSolutions',
        name: 'Digital Solutions',
        path: '/service-interaction/selfservice',
        disabled: false,
        children: [
          {
            key: 'summary',
            name: 'Summary',
            path: '/service-interaction/selfservice',
            disabled: false,
          },
          {
            key: 'edi',
            name: 'EDI',
            path: '/service-interaction/edi',
            disabled: false,
          },
          {
            key: 'precheckmyscript',
            name: 'PreCheck MyScript',
            path: '/service-interaction/precheck-myscript',
            disabled: false,
          },
          {
            key: 'smartedits',
            name: 'Smart Edits',
            path: '/service-interaction/smart-edits',
            disabled: false,
          },
        ],
      },
    ],
  },
  {
    key: 'ancillary',
    name: 'Ancillary',
    path: ROUTES_PATHS.Ancillary.Root,
    disabled: false,
    hasDropdown: true,
    access: [[Logins.Internal, Views.General, Modes.General]],
    children: [
      {
        key: 'labs',
        name: 'Labs',
        path: ROUTES_PATHS.Ancillary.Labs,
        disabled: false,
      },
      {
        key: 'imaging',
        name: 'Imaging',
        path: ROUTES_PATHS.Ancillary.Imaging,
        disabled: false,
      },
    ],
  },
  {
    key: 'surest',
    name: 'Surest',
    path: '/surest',
    disabled: false,
    hasDropdown: false,
    access: [[Logins.Internal, Views.General, Modes.General]],
  },
  {
    key: 'contenteditor',
    name: 'Content Editor',
    path: ROUTES_PATHS.ContentEditor.root,
    disabled: false,
    hasDropdown: true,
    access: [[Logins.Internal, Views.General, Modes.General]],
    children: [
      {
        key: 'faqDocumentation',
        name: 'FAQ Documentation',
        path: ROUTES_PATHS.ContentEditor.FAQ,
        disabled: false,
      },
      {
        key: 'knowledgeCenter',
        name: 'Knowledge Center',
        path: ROUTES_PATHS.ContentEditor.root,
        disabled: false,
      },
      {
        key: 'onboarding',
        name: 'Onboarding',
        path: ROUTES_PATHS.ContentEditor.Onboarding,
        disabled: false,
      },
      {
        key: 'notificationBanner',
        name: 'Notification Banner Manager',
        path: ROUTES_PATHS.ContentEditor.Notification,
        disabled: false,
      },
    ],
  },
  {
    key: 'medicalcost',
    name: 'Medical Cost Affordability',
    path: '/medical-cost',
    disabled: false,
    hasDropdown: false,
    access: [[Logins.Internal, Views.GeoSegmentation, Modes.General]],
  },
  {
    key: 'scoreCard',
    name: 'Contract Intelligence',
    path: ROUTES_PATHS.ContractIntelligence.Scorecard,
    disabled: false,
    hasDropdown: true,
    access: [
      [Logins.Internal, Views.ScoreCard, Modes.General],
      [Logins.Internal, Views.MedicalCost, Modes.General],
      [Logins.Internal, Views.Dashboards, Modes.General],
      [Logins.Internal, Views.ProviderBriefing, Modes.General],
      [Logins.Internal, Views.ProviderExperience, Modes.General],
    ],
    children: [
      {
        key: 'Summary',
        name: 'Summary',
        path: ROUTES_PATHS.ContractIntelligence.Scorecard,
        disabled: false,
      },
      {
        key: 'HeatMap',
        name: 'Heatmap',
        path: ROUTES_PATHS.ContractIntelligence.HeatMap,
        disabled: false,
      },
    ],
  },
  {
    key: 'affordabilityDrivers',
    name: 'Affordability Drivers',
    path: '/medical-cost',
    disabled: false,
    hasDropdown: false,
    access: [
      [Logins.Internal, Views.ScoreCard, Modes.General],
      [Logins.Internal, Views.MedicalCost, Modes.General],
      [Logins.Internal, Views.Dashboards, Modes.General],
      [Logins.Internal, Views.ProviderBriefing, Modes.General],
      [Logins.Internal, Views.ProviderExperience, Modes.General],
    ],
  },
  {
    key: 'providerExperienceMenu',
    name: 'Provider Experience',
    path: ROUTES_PATHS.Providers.Experience,
    disabled: false,
    hasDropdown: false,
    access: [
      [Logins.Internal, Views.ScoreCard, Modes.General],
      [Logins.Internal, Views.MedicalCost, Modes.General],
      [Logins.Internal, Views.Dashboards, Modes.General],
      [Logins.Internal, Views.ProviderBriefing, Modes.General],
      [Logins.Internal, Views.ProviderExperience, Modes.General],
    ],
  },
  {
    key: 'dashboards',
    name: 'Operations',
    path: ROUTES_PATHS.Operations,
    disabled: false,
    hasDropdown: false,
    access: [
      [Logins.Internal, Views.ScoreCard, Modes.General],
      [Logins.Internal, Views.MedicalCost, Modes.General],
      [Logins.Internal, Views.Dashboards, Modes.General],
      [Logins.Internal, Views.ProviderBriefing, Modes.General],
      [Logins.Internal, Views.ProviderExperience, Modes.General],
    ],
  },
  {
    key: 'providerBriefingMenu',
    name: 'Provider Negotiation Briefing',
    path: ROUTES_PATHS.Providers.Briefing,
    disabled: false,
    hasDropdown: false,
    access: [
      [Logins.Internal, Views.ScoreCard, Modes.General],
      [Logins.Internal, Views.MedicalCost, Modes.General],
      [Logins.Internal, Views.Dashboards, Modes.General],
      [Logins.Internal, Views.ProviderBriefing, Modes.General],
      [Logins.Internal, Views.ProviderExperience, Modes.General],
    ],
  },
  {
    key: 'psms',
    name: 'PSMS',
    path: ROUTES_PATHS.PSMS.Summary,
    disabled: false,
    hasDropdown: true,
    children: [
      {
        key: 'summary',
        name: 'Summary',
        path: ROUTES_PATHS.PSMS.Summary,
        disabled: false,
      },
      {
        key: 'claims',
        name: 'Claims',
        disabled: false,
        path: ROUTES_PATHS.PSMS.Claims,
      },
      {
        key: 'digitalAdoption',
        name: 'Digital Adoption',
        disabled: false,
        path: ROUTES_PATHS.PSMS.Summary,
      },
      {
        key: 'priorAuthorization',
        name: 'Prior Authorization',
        disabled: false,
        path: ROUTES_PATHS.PSMS.PriorAuthorization,
      },
      {
        key: 'paymentIntegrity',
        name: 'Payment Integrity',
        disabled: false,
        path: ROUTES_PATHS.PSMS.PaymentIntegrity,
      },
      {
        key: 'accountsReceivable',
        name: 'Accounts Receivable',
        disabled: false,
        path: ROUTES_PATHS.PSMS.AccountsReceivable,
      },
      {
        key: 'chatCall',
        name: 'Chat & Call',
        disabled: false,
        path: ROUTES_PATHS.PSMS.ChatAndCalls,
      },
    ],
    access: [
      [Logins.Internal, Views.General, Modes.Simulation],
      [Logins.External, Views.General, Modes.General],
      [Logins.External, Views.General, Modes.Simulation],
    ],
  },
  {
    key: 'pemMenu',
    name: 'Provider Effort',
    path: ROUTES_PATHS.Providers.PEM.PEMDetails,
    disabled: false,
    hasDropdown: true,
    access: [[Logins.Internal, Views.General, Modes.General]],
    children: [
      {
        key: 'pemDetails',
        name: 'Provider Effort & Impact Score',
        path: ROUTES_PATHS.Providers.PEM.PEMDetails,
        disabled: false,
      },
      {
        key: 'pemSummary',
        name: 'Summary',
        path: ROUTES_PATHS.Providers.PEM.Summary,
        disabled: false,
      },
    ],
  },
];

// Second Row Navigation Menus (Reporting and Provider Management)
export const SecondaryNavigationMenus: MenuItem[] = [
  {
    key: 'providerPerformance',
    name: 'Provider Performance',
    path: '/overview-page-advocate/health-system-details',
    disabled: false,
    hasDropdown: true,
    children: [
      {
        key: 'providerPerformance',
        name: 'Provider Performance',
        path: ROUTES_PATHS.Overview.HealthSystem,
        disabled: false,
      },
      {
        key: 'geoSegmentation',
        name: 'Geographic Segmentation',
        path: '/',
        disabled: false,
      },
      {
        key: 'scoreCard',
        name: 'Contract Intelligence',
        path: ROUTES_PATHS.ContractIntelligence.Scorecard,
        disabled: false,
      },
      {
        key: 'medicalCost',
        name: 'Affordability Drivers',
        path: '/medical-cost',
        disabled: false,
      },
      {
        key: 'operations',
        name: 'Operations',
        path: ROUTES_PATHS.Operations,
        disabled: false,
      },
      {
        key: 'providerBriefing',
        name: 'Provider Negotiation Briefing',
        path: ROUTES_PATHS.Providers.Briefing,
        disabled: false,
      },
      {
        key: 'providerExperience',
        name: 'Provider Experience',
        path: ROUTES_PATHS.Providers.Experience,
        disabled: false,
      },
    ],
  },
  {
    key: 'representingProvider',
    name: '',
    path: '#',
    disabled: true,
    hasDropdown: false,
    access: [
      [Logins.Internal, Views.General, Modes.General],
      [Logins.Internal, Views.General, Modes.Simulation],
      [Logins.External, Views.General, Modes.General],
    ],
  },
  {
    key: 'corporateTaxIdOwner',
    name: 'Corporate Tax ID Owner',
    path: '#',
    disabled: true,
    hasDropdown: false,
    access: [
      [Logins.Internal, Views.General, Modes.Simulation],
      [Logins.External, Views.General, Modes.General],
    ],
  },
  {
    key: 'taxIdNumber',
    name: 'Tax ID Number',
    path: '#',
    disabled: true,
    hasDropdown: false,
    access: [
      [Logins.Internal, Views.General, Modes.Simulation],
      [Logins.External, Views.General, Modes.General],
    ],
  },
  {
    key: 'viewTaxId',
    name: 'View Tax ID Details',
    path: '/overview-page-advocate/health-system-details',
    disabled: false,
    hasDropdown: false,
    access: [[Logins.Internal, Views.General, Modes.General]],
  },
  {
    key: 'seeProviderView',
    name: 'See what this provider sees',
    path: '/ProviderView',
    disabled: false,
    hasDropdown: false,
    access: [[Logins.Internal, Views.General, Modes.General]],
  },
  {
    key: 'changeProviders',
    name: 'Change Providers',
    path: '/ChangeProviders',
    disabled: false,
    hasDropdown: false,
    access: [[Logins.External, Views.General, Modes.General]],
  },
];

/**
 * PSMS Page Navigation Menus
 * Custom menu configuration for PSMS page with Summary and Details sub-items
 */
export const PSMSNavigationMenus: MenuItem[] = [
  {
    key: 'myhome',
    name: 'Home',
    path: '/myhome',
    disabled: false,
    hasDropdown: false,
  },
  {
    key: 'psms-claims',
    name: 'Claims',
    path: ROUTES_PATHS.PSMS.Summary,
    disabled: false,
    hasDropdown: true,
    children: [
      {
        key: 'claims-summary',
        name: 'Summary',
        path: ROUTES_PATHS.PSMS.Summary,
        disabled: false,
      },
      {
        key: 'claims-details',
        name: 'Details',
        path: ROUTES_PATHS.PSMS.Claims,
        disabled: false,
      },
    ],
  },
  {
    key: 'digital-adoption',
    name: 'Digital Adoption',
    path: ROUTES_PATHS.PSMS.Summary,
    disabled: isMultiTinPage && !isSingleExtendedTinSelected ? true : false,
    hasDropdown: true,
    children: [
      {
        key: 'digital-adoption-summary',
        name: 'Summary',
        path: ROUTES_PATHS.PSMS.Summary,
        disabled: isMultiTinPage && !isSingleExtendedTinSelected ? true : false,
      },
    ],
  },
  {
    key: 'prior-auth',
    name: 'Prior Authorization',
    path: ROUTES_PATHS.PSMS.Summary,
    disabled: false,
    hasDropdown: true,
    children: [
      {
        key: 'prior-auth-summary',
        name: 'Summary',
        path: ROUTES_PATHS.PSMS.Summary,
        disabled: false,
      },
      {
        key: 'prior-auth-details',
        name: 'Details',
        path: ROUTES_PATHS.PSMS.PriorAuthorization,
        disabled: false,
      },
    ],
  },
  {
    key: 'payment-integrity',
    name: 'Payment Integrity',
    path: ROUTES_PATHS.PSMS.Summary,
    disabled: false,
    hasDropdown: true,
    children: [
      {
        key: 'payment-integrity-summary',
        name: 'Summary',
        path: ROUTES_PATHS.PSMS.Summary,
        disabled: false,
      },
      {
        key: 'payment-integrity-details',
        name: 'Details',
        path: ROUTES_PATHS.PSMS.PaymentIntegrity,
        disabled: false,
      },
    ],
  },
  {
    key: 'accounts-receivable',
    name: 'Accounts Receivable',
    path: ROUTES_PATHS.PSMS.Summary,
    disabled: false,
    hasDropdown: true,
    children: [
      {
        key: 'accounts-receivable-summary',
        name: 'Summary',
        path: ROUTES_PATHS.PSMS.Summary,
        disabled: false,
      },
      {
        key: 'accounts-receivable-details',
        name: 'Details',
        path: ROUTES_PATHS.PSMS.AccountsReceivable,
        disabled: false,
      },
    ],
  },
  {
    key: 'chat-call',
    name: 'Chat /Call',
    path: ROUTES_PATHS.PSMS.Summary,
    disabled: false,
    hasDropdown: true,
    children: [
      {
        key: 'chat-call-summary',
        name: 'Summary',
        path: ROUTES_PATHS.PSMS.Summary,
        disabled: false,
      },
      {
        key: 'chat-call-details',
        name: 'Details',
        path: ROUTES_PATHS.PSMS.ChatAndCalls,
        disabled: false,
      },
    ],
  },
  {
    key: 'inquiries-issues-routing',
    name: 'Inquiries/Issues Routing',
    path: ROUTES_PATHS.PSMS.Summary,
    disabled: false,
    hasDropdown: true,
    children: [
      {
        key: 'inquiries-issues-routing-summary',
        name: 'Summary',
        path: ROUTES_PATHS.PSMS.Summary,
        disabled: false,
      },
    ],
  },
  {
    key: 'prc',
    name: 'PRC',
    path: ROUTES_PATHS.PSMS.Summary,
    disabled: false,
    hasDropdown: true,
    children: [
      {
        key: 'prc-summary',
        name: 'Summary',
        path: ROUTES_PATHS.PSMS.Summary,
        disabled: false,
      },
      {
        key: 'prc-details',
        name: 'Details',
        path: ROUTES_PATHS.PSMS.PRC,
        disabled: false,
      },
    ],
  },
];

// PSMS-specific Secondary Navigation Menus (excludes Provider Performance and See what this provider sees)
export const PSMSSecondaryNavigationMenus: MenuItem[] = [
  {
    key: 'representingProvider',
    name: '',
    path: '#',
    disabled: true,
    hasDropdown: false,
    access: [[Logins.Internal, Views.General, Modes.General]],
  },
  {
    key: 'viewTaxId',
    name: 'View Tax ID Details',
    path: ROUTES_PATHS.PSMS.ViewTaxIdDetails,
    disabled: false,
    hasDropdown: false,
    access: [[Logins.Internal, Views.General, Modes.General]],
  },
  {
    key: 'changeProviders',
    name: 'Change Providers',
    path: '#',
    disabled: false,
    hasDropdown: false,
    access: [[Logins.Internal, Views.General, Modes.General]],
  },
  {
    key: 'seeProviderView',
    name: 'See what this provider sees',
    path: ROUTES_PATHS.PSMS.Summary,
    disabled: false,
    hasDropdown: false,
    access: [[Logins.Internal, Views.General, Modes.General]],
  },
];

/**
 * Filter PSMS menus based on user permissions or session data
 * Returns filtered PSMSNavigationMenus for the PSMS page
 */
export const getFilteredPSMSMenus = (options?: {
  disabledKeys?: string[];
  loginType?: string;
  modeType?: string;
}): MenuItem[] => {
  const disableMenuOptions = ['inquiries-issues-routing', 'prc'];
  const disabledKeys = options?.disabledKeys || [];
  const loginType = options?.loginType || Logins.Internal;
  const modeType = options?.modeType || Modes.General;

  // Hide certain menus for External users or Simulation mode
  const isExternalOrSimulation = loginType === Logins.External || modeType === Modes.Simulation;
  const externalSimulationHiddenMenus = isExternalOrSimulation
    ? ['payment-integrity', 'accounts-receivable', 'psms-claims']
    : [];

  // Filter menus based on access control and disabled keys
  const menus = PSMSNavigationMenus.filter((menu) => {
    // HIDE menus in hardcoded disableMenuOptions
    if (disableMenuOptions.includes(menu.key)) {
      return false;
    }
    // HIDE menus for external/simulation users
    if (externalSimulationHiddenMenus.includes(menu.key)) {
      return false;
    }

    return true;
  }).map((menu) => {
    // Create a copy to avoid mutating the original
    const menuCopy = { ...menu };

    // Mark menu as disabled if in disabledKeys
    if (disabledKeys.includes(menu.key)) {
      menuCopy.disabled = true;
    }

    const isPSMSReportCardClicked = sessionStorage.getItem('isPSMSReportCardClicked') === 'true';
    const isSimulationMode =
      sessionStorage.getItem('simulationMode') === 'true' || modeType === Modes.Simulation;

    // Change myhome path based on simulation mode and PSMS report card click status
    if (menuCopy.key === 'myhome') {
      // If simulation mode AND PSMS report card clicked, navigate to /PSMS
      if (isSimulationMode && isPSMSReportCardClicked) {
        menuCopy.path = '/PSMS';
      }
      // Otherwise, change to MyInsights for External users or Simulation mode (when NOT report card clicked)
      else if (isExternalOrSimulation && !isPSMSReportCardClicked) {
        menuCopy.path = ROUTES_PATHS.MyInsights;
      }
    }

    // Filter children if they exist
    if (menuCopy.children) {
      menuCopy.children = menuCopy.children
        .filter((child) => {
          // HIDE children based on hardcoded disableMenuOptions
          if (disableMenuOptions.includes(child.key)) {
            return false; // Remove from children array
          }
          return true; // Keep the child
        })
        .map((child) => {
          // Mark child as disabled if in disabledKeys
          if (disabledKeys.includes(child.key)) {
            return { ...child, disabled: true };
          }
          return child;
        });
    }

    return menuCopy;
  });

  return menus;
};

/**
 * Filter menus based on user permissions, roles, or session data
 * This mimics the GetMenus function from the original Angular hamburger-menu code
 */
export const getFilteredMenus = (options?: {
  isInternal?: boolean;
  isMultiTin?: boolean;
  userRoles?: string[];
  disabledKeys?: string[];
  selectedView?: string;
  loginType?: string;
  modeType?: string;
  geoSegmentFlag?: boolean;
  geoCriteria?: string | null;
  pcorData?: boolean; // PCOR data availability flag
  admData?: boolean; // ADM state/LOB data availability - controls the ADM child menu
  _admRole?: boolean; // Not used in React filtering - kept for API compatibility
  scoreCardRole?: boolean; // ScoreCard role (UHCI_SCORE_CARD) - controls ScoreCard menu
  pemRole?: boolean; // PEM role (UHCI_PEM) - controls Provider Experience menu
  affData?: boolean; // Affordability data availability - controls Network Management children
  cesfData?: boolean; // CESF data availability - controls Network Management children
  pocaEnabled?: boolean; // VIM status - controls Point of Care Assist menu visibility
}): MenuItem[] => {
  const {
    isMultiTin: _isMultiTin = false, // Kept for backward compatibility, but we read from sessionStorage instead
    disabledKeys = [],
    selectedView = Views.General,
    loginType = Logins.Internal,
    modeType = Modes.General,
    geoSegmentFlag = false,
    geoCriteria = null,
    pcorData = false,
    admData = false,
    _admRole = false, // Not used in React - kept for API compatibility
    scoreCardRole = false,
    pemRole = false,
    affData = false,
    cesfData = false,
    pocaEnabled = false, // Default to false - POCA hidden unless VIM status is true
  } = options || {};

  // Build access array like hamburger-menu: [LoginType, View, ModeType]
  const access = [loginType, selectedView, modeType];
  const isContractIdNavigation = sessionStorage.getItem('contractIdNavigation') === 'true';
  const isContractBulkFlow =
    isContractIdNavigation && sessionStorage.getItem('hiVolumeContractFlow') === 'true';

  // Special case: If GeoCriteria is not null, show specific menus for geo segmentation
  if (geoSegmentFlag && geoCriteria !== null && geoCriteria !== 'null') {
    // Build base menu array that includes Contract Intelligence
    // It will be filtered later based on scoreCardRole and geoCriteria value
    let geoMenus: MenuItem[] = [
      {
        key: 'dashboard',
        name: 'My Home',
        path: '/myhome',
        disabled: false,
        hasDropdown: false,
      },
      {
        key: 'reimbursements',
        name: 'Reimbursements',
        path: ROUTES_PATHS.Reimbursements.Processed,
        disabled: false,
        hasDropdown: true,
        children: [
          {
            key: 'grsummmary',
            name: 'Processed',
            path: ROUTES_PATHS.Reimbursements.Processed,
            disabled: false,
          },
          {
            key: 'payments',
            name: 'Payments',
            path: ROUTES_PATHS.Reimbursements.Payments,
            disabled: false,
          },
          {
            key: 'nonpayments',
            name: 'Non-Payments',
            path: ROUTES_PATHS.Reimbursements.NonPayments,
            disabled: false,
          },
          {
            key: 'reconsiderations',
            name: 'Reconsiderations',
            path: ROUTES_PATHS.Reimbursements.Reconsiderations,
            disabled: false,
          },
          {
            key: 'overpayments',
            name: 'Overpayments',
            path: ROUTES_PATHS.Reimbursements.OverPayments,
            disabled: false,
          },
          {
            key: 'paymentIntegrity',
            name: 'Payment Integrity',
            path: '/reimbursements/payment-integrity',
            disabled: false,
            children: [
              {
                key: 'medicalRecordEvaluations',
                name: 'Medical Record Evaluations',
                path: ROUTES_PATHS.Reimbursements.PaymentIntegrity,
                disabled: false,
              },
            ],
          },
          {
            key: 'settlements',
            name: 'Settlements',
            path: '/reimbursements/settlements',
            disabled: false,
          },
        ],
      },
      {
        key: 'networkmanagement',
        name: 'Network Management',
        path: '/network-management',
        disabled: false,
        hasDropdown: true,
        children: [
          {
            key: 'exemption',
            name: 'Exemption Summary',
            path: ROUTES_PATHS.NetworkManagement.ExemptSummary,
            disabled: false,
          },
          {
            key: 'vbc',
            name: 'Value Based Contracting',
            path: ROUTES_PATHS.NetworkManagement.VBC,
            disabled: false,
          },
          {
            key: 'cro',
            name: 'Contract Renewal & Remediation',
            path: ROUTES_PATHS.NetworkManagement.ContractRemidiation,
            disabled: false,
          },
          {
            key: 'redirections',
            name: 'Site of Service Redirections',
            path: ROUTES_PATHS.NetworkManagement.Redirections,
            disabled: false,
          },
        ],
      },
      {
        key: 'priorauth',
        name: 'Prior Auth Summary',
        path: ROUTES_PATHS.PriorAuth.Summary,
        disabled: false,
        hasDropdown: false,
      },
      {
        key: 'serviceinteraction',
        name: 'Service Interaction',
        path: '/service-interaction',
        disabled: false,
        hasDropdown: true,
        children: [
          {
            key: 'calls',
            name: 'Calls',
            path: '/service-interaction/calls',
            disabled: false,
          },
          {
            key: 'digitalSolutions',
            name: 'Digital Solutions',
            path: '/service-interaction/selfservice',
            disabled: false,
            children: [
              {
                key: 'edi',
                name: 'EDI',
                path: '/service-interaction/edi',
                disabled: false,
              },
              {
                key: 'smartedits',
                name: 'Smart Edits',
                path: '/service-interaction/smart-edits',
                disabled: false,
              },
              {
                key: 'precheckmyscript',
                name: 'PreCheck MyScript',
                path: '/service-interaction/precheck-myscript',
                disabled: false,
              },
            ],
          },
        ],
      },
      {
        key: 'scoreCard',
        name: 'Contract Intelligence',
        path: ROUTES_PATHS.ContractIntelligence.Scorecard,
        disabled: false,
        hasDropdown: true,
        children: [
          {
            key: 'Summary',
            name: 'Summary',
            path: ROUTES_PATHS.ContractIntelligence.Scorecard,
            disabled: false,
          },
          {
            key: 'HeatMap',
            name: 'Heatmap',
            path: ROUTES_PATHS.ContractIntelligence.HeatMap,
            disabled: false,
          },
        ],
      },
      {
        key: 'medicalcost',
        name: 'Medical Cost Affordability',
        path: '/medical-cost',
        disabled: false,
        hasDropdown: false,
      },
      {
        key: 'pemMenu',
        name: 'Provider Effort',
        path: ROUTES_PATHS.Providers.PEM.PEMDetails,
        disabled: false,
        hasDropdown: true,
        access: [[Logins.Internal, Views.General, Modes.General]],
        children: [
          {
            key: 'pemDetails',
            name: 'Provider Effort & Impact Score',
            path: ROUTES_PATHS.Providers.PEM.PEMDetails,
            disabled: false,
          },
          {
            key: 'pemSummary',
            name: 'Summary',
            path: ROUTES_PATHS.Providers.PEM.Summary,
            disabled: false,
          },
        ],
      },
    ];

    // Apply scoreCardRole filtering - HIDE ScoreCard if no scoreCardRole
    if (!scoreCardRole) {
      geoMenus = geoMenus.filter((m) => m.key !== 'scoreCard');
    }

    // Apply GeoCriteria filtering - HIDE ScoreCard for REGION or STATE
    if (geoCriteria === 'REGION' || geoCriteria === 'STATE') {
      geoMenus = geoMenus.filter((m) => m.key !== 'scoreCard');
    }

    // Apply pemRole filtering to new PEM Details menu in Geo flow
    if (!pemRole) {
      geoMenus = geoMenus.filter((m) => m.key !== 'pemMenu');
    }

    // HIDE new PEM menu when the new PEM page is disabled (Geo flow)
    if (!PEM_FEATURE_ENABLED) {
      geoMenus = geoMenus.filter((m) => m.key !== 'pemMenu');
    }

    // HIDE CRO menu item when CRO feature is disabled (Geo flow)
    if (!CRO_FEATURE_ENABLED) {
      geoMenus = geoMenus.map((menu) => {
        if (menu.key !== 'networkmanagement' || !menu.children) return menu;
        return {
          ...menu,
          children: menu.children.filter((child) => child.key !== 'cro'),
        };
      });
    }

    return geoMenus;
  }

  //Check if PSMSTinAssociated is true in sessionStorage for single-TIN users to show PSMS menu in See Provider View
  const isPSMSTinAssociated =
    sessionStorage.getItem(SESSION_KEYS.IS_PSMS_TIN_ASSOCIATED) === 'true' ? true : false;
  const psmsFeatureFlag =
    sessionStorage.getItem(SESSION_KEYS.PSMS_FEATURE_FLAG) === 'Y' ? true : false;

  // Special case: If 'seeProviderView' is selected, show only the required menus with dropdowns
  if (selectedView === 'See what this provider sees') {
    return [
      {
        key: 'myInsights',
        name: 'My Insights',
        path: ROUTES_PATHS.MyInsights,
        disabled: false,
        hasDropdown: false,
      },
      {
        key: 'priorauth',
        name: 'Prior Authorizations',
        path: ROUTES_PATHS.PriorAuth.Summary,
        disabled: false,
        hasDropdown: true,
        children: [
          {
            key: 'passummary',
            name: 'Summary',
            path: ROUTES_PATHS.PriorAuth.Summary,
            disabled: false,
          },
          {
            key: 'goldCard',
            name: 'Exemption Programs',
            path: ROUTES_PATHS.PriorAuth.GoldCard,
            disabled: false,
          },
        ],
      },
      {
        key: 'serviceinteraction',
        name: 'Service Interaction',
        path: '/service-interaction',
        disabled: false,
        hasDropdown: true,
        children: [
          {
            key: 'calls',
            name: 'Calls',
            path: '/service-interaction/calls',
            disabled: false,
          },
          {
            key: 'digitalSolutions',
            name: 'Digital Solutions',
            path: '/service-interaction/selfservice',
            disabled: false,
            children: [
              {
                key: 'summary',
                name: 'Summary',
                path: '/service-interaction/selfservice',
                disabled: false,
              },
              {
                key: 'edi',
                name: 'EDI',
                path: '/service-interaction/edi',
                disabled: false,
              },
              {
                key: 'precheckmyscript',
                name: 'PreCheck MyScript',
                path: '/service-interaction/precheck-myscript',
                disabled: false,
              },
              {
                key: 'smartedits',
                name: 'Smart Edits',
                path: '/service-interaction/smart-edits',
                disabled: false,
              },
            ],
          },
        ],
      },
      {
        key: 'psms',
        name: 'PSMS',
        path: ROUTES_PATHS.PSMS.Summary,
        disabled: false,
        hasDropdown: false,
      },
    ];
  }

  // Clone the menus to avoid mutating the original
  let menus = JSON.parse(JSON.stringify(NavigationMenus)) as MenuItem[];

  // Filter menus based on access pattern (like hamburger-menu GetMenus)
  menus = menus.filter((menuItem: MenuItem) => {
    // Remove PSMS unless both the feature flag is enabled and the TIN is associated
    if (menuItem.key === psmsMenu.PSMS && !(isPSMSTinAssociated && psmsFeatureFlag)) {
      return false;
    }

    // If menu has no access array, include it by default
    if (!menuItem.access || menuItem.access.length === 0) {
      return true;
    }

    // Check if current access matches any of the menu's access patterns
    const menuExists = menuItem.access.find((accessItem: string[]) => {
      return accessItem.every((element, index) => element === access[index]);
    });

    return menuExists !== undefined;
  });

  // Remove PSMS children for External users or Simulation mode
  // Show PSMS menu without dropdown for external/simulation users
  const isExternalOrSimulation = loginType === Logins.External || modeType === Modes.Simulation;
  if (isExternalOrSimulation) {
    menus = menus.map((menu) => {
      if (menu.key === 'psms') {
        return {
          ...menu,
          hasDropdown: false,
          children: undefined,
        };
      }
      return menu;
    });
  }

  // Filter Reimbursements children based on isHiVolumePage or Contract ID bulk flow
  // When true, HIDE: Payment Integrity, Settlements
  if (isHiVolumePage || isContractBulkFlow) {
    menus = menus.map((menu) => {
      if (menu.key === 'reimbursements' && menu.children) {
        menu.children = menu.children.filter((child) => {
          const hiddenKeys = ['paymentIntegrity', 'settlements'];
          return !hiddenKeys.includes(child.key);
        });
      }
      return menu;
    });
  }

  // Filter Service Interaction children based on isHiVolumePage or Contract ID bulk flow
  // When true, HIDE: Summary and PreCheck MyScript under Digital Solutions
  if (isHiVolumePage || isContractBulkFlow) {
    menus = menus.map((menu) => {
      if (menu.key === 'serviceinteraction' && menu.children) {
        menu.children = menu.children.map((child) => {
          if (child.key === 'digitalSolutions' && child.children) {
            child.children = child.children.filter(
              (grandChild) => grandChild.key !== 'summary' && grandChild.key !== 'precheckmyscript'
            );
          }
          return child;
        });
      }
      return menu;
    });
  }

  // Find menu indexes for role-based filtering
  const admMenuIndex: number = menus.findIndex(
    (menuItem: MenuItem) => menuItem.key === 'networkmanagement'
  );
  const scoreCardIndex: number = menus.findIndex(
    (menuItem: MenuItem) => menuItem.key === 'scoreCard'
  );
  const pcorMenuIndex: number = menus.findIndex((menuItem: MenuItem) => menuItem.key === 'pcor');

  // If TIN is not associated with PSMS, HIDE the PSMS menu entirely
  if (!isPSMSTinAssociated) {
    menus = menus.filter((menu) => menu.key !== 'psms');
  }
  // Multi-TIN Navigation logic
  const multiTinNavigation = sessionStorage.getItem('multiTinNavigation');
  const multiSelectedTins = sessionStorage.getItem('multiSelectedTins');

  if (multiTinNavigation === 'true' && multiSelectedTins) {
    const tinCount = multiSelectedTins.split(',').length;

    if (tinCount > 1) {
      // Multiple TINs selected - HIDE menus that are not in the allowed list
      const allowedNamesForMultiTin = [
        'Performance',
        'My Home',
        'NPS and Satisfaction',
        'Reimbursements',
        'Prior Authorizations',
        'Network Management', // Added - needs to reach children filtering logic
        'Contract Intelligence', // Views.ScoreCard
        'Affordability Drivers', // Views.MedicalCost
        'Operations', // Views.Dashboards
        'Provider Negotiation Briefing', // Views.ProviderBriefing
        'Provider Experience', // Views.ProviderExperience
        'Provider Effort', // New PEM Details menu
        'Overview',
        'Service Interaction',
        'Calls',
        'Value Based Trades',
        'Surest',
      ];

      menus = menus.filter((menu) => {
        // Keep menus that are in the allowed list
        if (allowedNamesForMultiTin.includes(menu.name)) {
          return true;
        }
        // HIDE all other menus (return false to filter out)
        return false;
      });

      // For Reimbursements, filter out pisummary child
      menus = menus.map((menu) => {
        if (menu.name === 'Reimbursements' && menu.children) {
          menu.children = menu.children.filter((child) => child.key !== 'pisummary');
        }
        return menu;
      });

      // For Network Management, handle role-based children filtering
      if (admMenuIndex > 0) {
        const networkMenu = menus[admMenuIndex];
        if (networkMenu && networkMenu.children) {
          // Filter children: For multi-TIN, ONLY keep VBC, Affordability, and Exemption
          // ADM is NOT in the whitelist for multi-TIN
          networkMenu.children = networkMenu.children.filter((child) => {
            // HIDE ADM - it's not in the multi-TIN whitelist
            if (child.key === 'adm') {
              return false; // ADM not allowed in multi-TIN at all
            }
            // VBC is ALWAYS visible in multi-TIN mode
            if (child.key === 'vbc' || child.name === 'Value Based Contracting') {
              return true; // Always show VBC in multi-TIN
            }
            // CRO is ALWAYS visible in multi-TIN mode
            if (child.key === 'cro' || child.name === 'Contract Renewal & Remediation') {
              return CRO_FEATURE_ENABLED;
            }
            if (child.key === 'affordability' || child.name === 'Affordability Programs') {
              return affData; // Affordability: show if affData
            }
            if (child.key === 'exemption' || child.name === 'Exemption Summary') {
              return affData || cesfData; // Exemption: show if affData OR cesfData
            }
            // Hide all other children
            return false;
          });

          // If no children remain, hide the entire Network Management menu
          if (networkMenu.children.length === 0) {
            menus = menus.filter((m) => m.key !== 'networkmanagement');
          }
        }
      }
    } else if (tinCount === 1) {
      // Single TIN selected - filter pisummary in Reimbursements
      menus = menus.map((menu) => {
        if (menu.name === 'Reimbursements' && menu.children) {
          menu.children = menu.children.filter((child) => child.key !== 'pisummary');
        }
        return menu;
      });
    }
  } else {
    // No multi-TIN navigation - apply standard filtering
    // For non-multi-TIN, filter pisummary from Reimbursements if not in GeoCriteria mode
    if (!geoSegmentFlag || !geoCriteria || geoCriteria === 'null') {
      menus = menus.map((menu) => {
        if (menu.name === 'Reimbursements' && menu.children) {
          menu.children = menu.children.filter((child) => child.key !== 'pisummary');
        }
        return menu;
      });
    }
  }

  if (isContractIdNavigation) {
    // HIDE these menus entirely for Contract ID navigation (not just disable, since
    // `disabled` only grays items out - it does not remove them from the nav bar).
    // scoreCard/affordabilityDrivers/providerExperienceMenu/providerBriefingMenu must stay
    // visible when the user has switched to one of the Reporting View sub-views (3-6),
    // since those are exactly the primary items that correspond to those views.
    const isReportingSubView = [
      Views.ScoreCard,
      Views.MedicalCost,
      Views.Dashboards,
      Views.ProviderBriefing,
      Views.ProviderExperience,
    ].includes(selectedView);
    const hiddenMenuKeysForContractId = isReportingSubView
      ? [
          'pcor',
          'myInsights',
          'ancillary',
          'surest',
          'contenteditor',
          'medicalcost',
          'psms',
          'pemMenu',
        ]
      : [
          'pcor',
          'myInsights',
          'ancillary',
          'surest',
          'contenteditor',
          'medicalcost',
          'scoreCard',
          'affordabilityDrivers',
          'providerExperienceMenu',
          'providerBriefingMenu',
          'psms',
          'pemMenu',
        ];
    menus = menus.filter((menu) => !hiddenMenuKeysForContractId.includes(menu.key));

    // HIDE Exemption Programs under Prior Authorizations for Contract ID navigation
    menus = menus.map((menu) => {
      if (menu.key === 'priorauth' && menu.children) {
        return {
          ...menu,
          children: menu.children.filter((child) => child.key !== 'goldCard'),
        };
      }
      return menu;
    });
  }

  // Role-based menu filtering
  // Filter Network Management children based on data availability
  // DO NOT remove entire menu based solely on admRole - filter children first, then remove if empty
  if (admMenuIndex > 0) {
    const networkMenu = menus[admMenuIndex];
    if (networkMenu) {
      if (networkMenu.children) {
        // Contract ID normal (non-bulk) flow: always surface these two items directly,
        // bypassing the affData/cesfData availability gating below.
        const isContractIdNormalFlow = isContractIdNavigation && !isContractBulkFlow;
        // Filter children based on affData and cesfData
        networkMenu.children = networkMenu.children.filter((child) => {
          // HIDE Affordability Programs if no affData
          if (child.key === 'affordability') {
            return isContractIdNormalFlow || affData;
          }
          // HIDE Exemption Summary if no affData AND no cesfData
          if (child.key === 'exemption') {
            return isContractIdNormalFlow || affData || cesfData;
          }
          // VBC is ALWAYS visible
          if (child.key === 'vbc') {
            return true; // Always show VBC
          }
          // HIDE Redirections for multi-TIN and Contract ID flows (enabled only for HCO and Geo flows)
          if (child.key === 'redirections') {
            if (multiTinNavigation === 'true' || isContractIdNavigation) {
              return false;
            }
            return true;
          }
          // HIDE CRO menu item when CRO feature is disabled
          if (child.key === 'cro') {
            return CRO_FEATURE_ENABLED;
          }
          // HIDE ADM if the provider has no ADM state/LOB data
          if (child.key === 'adm') {
            if (isContractIdNavigation) {
              return false; // ADM not allowed for Contract ID navigation
            }
            const isSingleTin =
              multiTinNavigation === 'true' &&
              multiSelectedTins &&
              multiSelectedTins.split(',').length === 1;
            if (isSingleTin) {
              return false; // ADM not allowed in Single TIN flow
            }
            return admData;
          }
          return true;
        });

        // If no children remain, hide the entire Network Management menu
        if (networkMenu.children.length === 0) {
          menus = menus.filter((m) => m.key !== 'networkmanagement');
        }
      }
    }
  }

  // HIDE ScoreCard menu if no scoreCardRole
  if (scoreCardIndex > 0 && !scoreCardRole) {
    menus = menus.filter((m) => m.key !== 'scoreCard');
  }

  // HIDE Provider Experience menu if no pemRole
  if (!pemRole) {
    menus = menus.filter((m) => m.key !== 'providerExperienceMenu');
    menus = menus.filter((m) => m.key !== 'pemMenu');
  }

  // HIDE legacy Provider Experience menu when the new PEM page is enabled
  if (PEM_FEATURE_ENABLED) {
    menus = menus.filter((m) => m.key !== 'providerExperienceMenu');
  }

  // HIDE new PEM menu when the new PEM page is disabled
  if (!PEM_FEATURE_ENABLED) {
    menus = menus.filter((m) => m.key !== 'pemMenu');
  }

  // PCOR menu - HIDE if no pcorData, ENABLE if pcorData is available
  if (pcorMenuIndex >= 0) {
    if (!pcorData) {
      // HIDE PCOR menu if no pcorData
      menus = menus.filter((m) => m.key !== 'pcor');
    } else {
      // ENABLE PCOR menu if pcorData is available
      menus[pcorMenuIndex].disabled = false;
    }
  }

  const pocaMenuIndex: number = menus.findIndex((menuItem: MenuItem) => menuItem.key === 'poca');
  if (pocaMenuIndex > 0 && !pocaEnabled) {
    menus = menus.filter((m) => m.key !== 'poca');
  }

  // Geo criteria changes
  // HIDE ScoreCard for REGION or STATE geo criteria
  if (geoCriteria === 'REGION' || geoCriteria === 'STATE') {
    menus = menus.filter((menu) => menu.key !== 'scoreCard');
  }

  // High Volume TIN Flow or Contract ID Bulk Flow
  // HIDE all menus except Prior Authorizations, My Home, Reimbursements, and Service Interaction
  // (plus Overview for Contract ID bulk flow specifically)
  // NOTE: PEM is intentionally excluded here so it stays hidden in Bulk flow.
  if (isHiVolumePage || isContractBulkFlow) {
    if (selectedView === Views.General || selectedView === Views.GeoSegmentation) {
      menus = menus.filter(
        (menu) =>
          menu.name === 'Prior Authorizations' ||
          menu.name === 'My Home' ||
          menu.name === 'Reimbursements' ||
          menu.name === 'Service Interaction' ||
          (isContractBulkFlow && menu.name === 'Overview')
      );
    }
    // For ScoreCard / MedicalCost / Dashboards / ProviderBriefing / ProviderExperience views
    // the access-based filter above already selected the correct menus
    // (My Home, Contract Intelligence, Affordability Drivers, Operations,
    //  Provider Negotiation Briefing, Provider Experience) — no extra filtering needed.
  }

  // HIDE Service Interaction menu if not enabled in environment
  const enableServiceInteractions = config('ENABLE_SERVICE_INTERACTIONS');
  if (!enableServiceInteractions && isHiVolumePage) {
    menus = menus.filter((menu) => menu.name !== 'Service Interaction');
  }

  // MARKET geo criteria - commented out
  // No action needed as it's commented out in Angular too

  // UHCI Vendor Changes
  // HIDE specific menus for vendor users
  let msad_groups: string[] = [];
  try {
    const msad_groups_string = sessionStorage.getItem('msad_groups');
    const msad_groups_val = msad_groups_string && atob(msad_groups_string);
    if (msad_groups_val) {
      msad_groups = JSON.parse(msad_groups_val);
    }
  } catch (error) {
    console.error('Error parsing msad_groups:', error);
  }

  const isVendor = msad_groups.includes('Vendor');
  if (isVendor) {
    const menusToHideForVendor = [
      'My Home',
      'Contract Intelligence', // Views.ScoreCard
      'Affordability Drivers', // Views.MedicalCost
      'Operations', // Views.Dashboards
      'Provider Experience', // Views.ProviderExperience
    ];

    menus = menus.filter((menu) => !menusToHideForVendor.includes(menu.name));
  }

  // HIDE menus based on disabledKeys
  menus = menus.filter((menu) => !disabledKeys.includes(menu.key));

  // Filter children based on disabledKeys
  menus = menus.map((menu) => {
    if (menu.children) {
      menu.children = menu.children
        .filter((child) => {
          // NEVER hide passummary (Prior Auth Summary) - it's always visible
          if (child.key === 'passummary') {
            return true;
          }
          // HIDE children based on disabledKeys
          if (disabledKeys.includes(child.key)) {
            return false;
          }
          return true;
        })
        .map((child) => {
          // Mark child as disabled if it has nested children (for UI behavior only)
          if (child.children && child.children.length > 0) {
            child.disabled = true;
          }
          return child;
        });
    }
    return menu;
  });

  // Gold Card filtering logic
  // goldCardFeatureToggle is 'true' in ALL environments (Angular constant — confirmed static).
  // In standalone React, Angular never writes this to sessionStorage, so we skip the toggle
  // check entirely and apply only the multi-TIN rule (same logic Angular used after toggle passed).
  menus = menus.map((menu) => {
    if (menu.key === 'priorauth' && menu.children) {
      menu.children = menu.children.filter((child) => {
        // passummary (Summary) is ALWAYS visible - never filter it out
        if (child.key === 'passummary') {
          return true;
        }

        // goldCard filtering logic
        if (child.key === 'goldCard') {
          // SHOW for single TIN / HCO / multi-TIN navigation
          return true;
        }

        return true;
      });
    }
    return menu;
  });

  // Suppress Content Editor from navigation bar
  menus = menus.filter((menu) => {
    if (menu.key === 'contenteditor') {
      return false;
    }
    return true;
  });

  // Filter out Network Management if user does not have required role
  menus = menus.filter((menu) => {
    if (menu.key === MENU_KEYS.NETWORK_MANAGEMENT && !hasNetworkManagementAccess()) {
      return false;
    }
    return true;
  });

  // Rename "Point of Care Assist" to "EMR - Point of Care Assist"
  // for Simulation mode OR External users
  if (modeType === Modes.Simulation || loginType === Logins.External) {
    menus = menus.map((menu) => {
      if (menu.key === 'poca') {
        return {
          ...menu,
          name: 'EMR - Point of Care Assist',
        };
      }
      return menu;
    });
  }

  return menus;
};

/**
 * Filter secondary navigation menus based on selected view
 */
export const getFilteredSecondaryMenus = (options?: {
  isInternal?: boolean;
  disabledKeys?: string[];
  selectedView?: string;
  loginType?: string;
  modeType?: string;
  geoSegmentFlag?: boolean;
  geoCriteria?: string | null;
  isMultiTin?: boolean;
  externalProvidersCount?: boolean; // True when multiple providers available
  pemRole?: boolean;
}): MenuItem[] => {
  const {
    disabledKeys = [],
    selectedView = Views.General,
    loginType = Logins.Internal,
    modeType = Modes.General,
    geoSegmentFlag = false,
    geoCriteria = null,
    externalProvidersCount = false,
    pemRole = false,
  } = options || {};

  // Build access array like hamburger-menu: [LoginType, View, ModeType]
  const access = [loginType, selectedView, modeType];
  const isContractIdNavigation = sessionStorage.getItem('contractIdNavigation') === 'true';

  // Special case: If GeoCriteria is not null, show only Reporting View and Change Providers
  if (geoSegmentFlag && geoCriteria !== null && geoCriteria !== 'null') {
    // Build the dropdown children array
    let dropdownChildren: SubMenuItem[] = [
      {
        key: 'providerPerformance',
        name: 'Provider Performance',
        path: ROUTES_PATHS.Overview.HealthSystem,
        disabled: false,
      },
      {
        key: 'geoSegmentation',
        name: 'Geographic Segmentation',
        path: '/',
        disabled: false,
      },
      {
        key: 'scoreCard',
        name: 'Contract Intelligence',
        path: ROUTES_PATHS.ContractIntelligence.Scorecard,
        disabled: false,
      },
      {
        key: 'medicalCost',
        name: 'Affordability Drivers',
        path: '/medical-cost',
        disabled: false,
      },
      {
        key: 'operations',
        name: 'Operations',
        path: ROUTES_PATHS.Operations,
        disabled: false,
      },
      {
        key: 'providerBriefing',
        name: 'Provider Negotiation Briefing',
        path: ROUTES_PATHS.Providers.Briefing,
        disabled: false,
      },
      {
        key: 'providerExperience',
        name: 'Provider Experience',
        path: ROUTES_PATHS.Providers.Experience,
        disabled: false,
      },
    ];

    // Angular Logic (checkViewList method - hamburger-menu.component.ts lines 2244-2253):
    // When GeoCriteria is not 'null' (REGION/STATE): Remove 'Affordability Drivers', keep 'Contract Intelligence'
    // When GeoCriteria is 'MARKET': Remove both 'Affordability Drivers' AND 'Contract Intelligence'

    if (geoCriteria === 'MARKET') {
      // MARKET: Remove both Affordability Drivers AND Contract Intelligence
      dropdownChildren = dropdownChildren.filter(
        (item) => item.key !== 'medicalCost' && item.key !== 'scoreCard'
      );
    } else if (geoCriteria === 'REGION' || geoCriteria === 'STATE') {
      // REGION/STATE: Remove only Affordability Drivers, keep Contract Intelligence visible
      dropdownChildren = dropdownChildren.filter((item) => item.key !== 'medicalCost');
    }
    if (!pemRole) {
      dropdownChildren = dropdownChildren.filter((item) => item.key !== 'providerExperience');
    }
    // HIDE legacy Provider Experience from Reporting View when the new PEM page is enabled
    if (PEM_FEATURE_ENABLED) {
      dropdownChildren = dropdownChildren.filter((item) => item.key !== 'providerExperience');
    }

    return [
      {
        key: 'providerPerformance',
        name: 'Provider Performance',
        path: '#',
        disabled: false,
        hasDropdown: true,
        children: dropdownChildren,
      },
      {
        key: 'changeProviders',
        name: 'Change Providers',
        path: '/ChangeProviders',
        disabled: false,
        hasDropdown: false,
      },
    ];
  }

  // Special case: If 'See what this provider sees' is selected (Simulation mode), show Corporate Tax ID Owner and Tax ID Number
  // Angular logic: internalUser && activeProviderMode (Simulation mode)
  if (modeType === Modes.Simulation || selectedView === 'See what this provider sees') {
    return [
      {
        key: 'corporateTaxIdOwner',
        name: '', // Will be populated dynamically from selectedTinLevelData.name in NavigationBar.tsx
        path: '#',
        disabled: true,
        hasDropdown: false,
      },
      {
        key: 'taxIdNumber',
        name: '', // Will be populated dynamically from selectedTinLevelData.value in NavigationBar.tsx
        path: '#',
        disabled: false,
        hasDropdown: true,
        children: [], // Will be populated dynamically from tinLevelData API in NavigationBar.tsx
      },
    ];
  }

  // Special case: External user mode - show Corporate Tax ID Owner, Tax ID Number, and optionally Change Providers
  // Angular logic: !internalUser (External mode)
  // Angular condition for Change Providers: (disableChangeProvider || externalProvidersCount) && !AcoFlag && !similationModeVal && !enableTinLevelExpirience
  // For External users: disableChangeProvider = false, so we check externalProvidersCount
  if (loginType === Logins.External) {
    const menus: MenuItem[] = [
      {
        key: 'corporateTaxIdOwner',
        name: '', // Will be populated dynamically from selectedTinLevelData.name in NavigationBar.tsx
        path: '#',
        disabled: true,
        hasDropdown: false,
      },
      {
        key: 'taxIdNumber',
        name: '', // Will be populated dynamically from selectedTinLevelData.value in NavigationBar.tsx
        path: '#',
        disabled: false,
        hasDropdown: true,
        children: [], // Will be populated dynamically from tinLevelData API in NavigationBar.tsx
      },
    ];

    // Add "Change Providers" only if externalProvidersCount is true (multiple providers available)
    // Angular: externalProvidersCount = currentUser[0].Providers.length > 1
    if (externalProvidersCount) {
      menus.push({
        key: 'changeProviders',
        name: 'Change Providers',
        path: '/ChangeProviders',
        disabled: false,
        hasDropdown: false,
      });
    }

    return menus;
  }

  // Clone the menus to avoid mutating the original
  let menus = JSON.parse(JSON.stringify(SecondaryNavigationMenus)) as MenuItem[];
  if (sessionStorage.getItem('contractIdNavigation') === 'true') {
    menus = menus.map((menu) =>
      menu.key === 'viewTaxId' ? { ...menu, path: ROUTES_PATHS.ContractIdDetails } : menu
    );
  }
  // HIDE Provider Experience from Reporting View dropdown if no pemRole (matches Angular hideViews logic)
  if (!pemRole) {
    menus = menus.map((menu) => {
      if (menu.key === 'providerPerformance' && menu.children) {
        menu.children = menu.children.filter((child) => child.key !== 'providerExperience');
      }
      return menu;
    });
  }
  // HIDE legacy Provider Experience from Reporting View when the new PEM page is enabled
  if (PEM_FEATURE_ENABLED) {
    menus = menus.map((menu) => {
      if (menu.key === 'providerPerformance' && menu.children) {
        menu.children = menu.children.filter((child) => child.key !== 'providerExperience');
      }
      return menu;
    });
  }
  // Filter menus based on access pattern
  menus = menus.filter((menuItem: MenuItem) => {
    // If menu has no access array, include it by default
    if (!menuItem.access || menuItem.access.length === 0) {
      return true;
    }

    // Check if current access matches any of the menu's access patterns
    const menuExists = menuItem.access.find((accessItem: string[]) => {
      return accessItem.every((element, index) => element === access[index]);
    });

    return menuExists !== undefined;
  });

  // Add "Change Providers" for internal users if selected view is Provider Performance or Geographic Segmentation
  if (
    (selectedView === 'Provider Performance' || selectedView === 'Geographic Segmentation') &&
    loginType === Logins.Internal &&
    !menus.some((menu) => menu.key === 'changeProviders')
  ) {
    menus.push({
      key: 'changeProviders',
      name: 'Change Providers',
      path: '/ChangeProviders',
      disabled: false,
      hasDropdown: false,
    });
  }

  // Add "Impact" button for internal users in Provider Performance / Geographic Segmentation
  // Only show when not in multi-TIN (>1 TIN) flow, not in geo segmentation flow, and not Contract ID navigation
  if (
    (selectedView === 'Provider Performance' || selectedView === 'Geographic Segmentation') &&
    loginType === Logins.Internal &&
    !menus.some((menu) => menu.key === 'impact') &&
    !isContractIdNavigation &&
    shouldShowImpact()
  ) {
    menus.push({
      key: 'impact',
      name: 'IMPACT',
      path: '#',
      disabled: false,
      hasDropdown: false,
    });
  }

  // Filter out "Change Providers" if selected view is NOT "Provider Performance" or "Geographic Segmentation"
  if (selectedView !== 'Provider Performance' && selectedView !== 'Geographic Segmentation') {
    menus = menus.filter((menu) => menu.key !== 'changeProviders');
  }

  // Filter out "See what this provider sees" based on Angular conditions:
  // *ngIf="internalUser && !activeProviderMode && (!isMultiTinVal || !isExtendedMultiTinVal) && !geoSegmentFlag"
  // Show when: Internal + Not in simulation + (NOT multi-TIN OR NOT extended multi-TIN) + Not in geo flow
  // Hide ONLY when: Extended multi-TIN (multi-TIN with multiple TINs selected)
  const multiTinNavigation = sessionStorage.getItem('multiTinNavigation');
  const multiSelectedTins = sessionStorage.getItem('multiSelectedTins');
  const isMultiTinVal = multiTinNavigation === 'true';
  const tinsArray = multiSelectedTins ? multiSelectedTins.split(',') : [];
  const isExtendedMultiTinVal = isMultiTinVal && tinsArray.length > 1;
  const activeProviderMode = sessionStorage.getItem('simulationMode') === 'true';
  const isInternal = loginType === Logins.Internal;

  // Show "See what this provider sees" when: Internal + Not in simulation + (!isMultiTinVal || !isExtendedMultiTinVal) + Not in geo flow + Not Contract ID navigation
  // This shows for: Single TIN, Multi-TIN with 1 TIN selected
  // This hides for: Extended Multi-TIN (multi-TIN with multiple TINs), Contract ID navigation
  const shouldShowSeeProviderView =
    isInternal &&
    !activeProviderMode &&
    (!isMultiTinVal || !isExtendedMultiTinVal) &&
    !geoSegmentFlag &&
    !isContractIdNavigation;

  if (!shouldShowSeeProviderView) {
    menus = menus.filter((menu) => menu.key !== 'seeProviderView');
  }

  // HIDE specific menus based on disabledKeys instead of disabling
  menus = menus
    .filter((menu) => {
      // Return false to hide (remove) menus in disabledKeys
      if (disabledKeys.includes(menu.key)) {
        return false;
      }
      return true;
    })
    .map((menu) => {
      // Filter children if they exist
      if (menu.children) {
        menu.children = menu.children
          .filter((child) => {
            // HIDE children based on disabledKeys
            if (disabledKeys.includes(child.key)) {
              return false; // Remove from children array
            }
            return true; // Keep the child
          })
          .map((child) => {
            // Mark child as disabled if it has nested children (sub-sub-categories)
            // This is for UI behavior, not filtering
            if (child.children && child.children.length > 0) {
              child.disabled = true;
            }
            return child;
          });
      }

      return menu;
    });

  // Show the selected Contract ID (or "Custom List" for multiple) under "You are representing" for Contract ID navigation
  if (isContractIdNavigation) {
    let individual: { ContractId?: string }[] | undefined;
    try {
      individual = JSON.parse(sessionStorage.getItem('contractIdDetails') || 'null')?.individual;
    } catch {
      individual = undefined;
    }

    if (Array.isArray(individual) && individual.length > 1) {
      menus = menus.map((menu) =>
        menu.key === 'representingProvider' ? { ...menu, name: 'Custom List' } : menu
      );
    } else {
      const selectedContractIdValue = sessionStorage.getItem('selectedContractId') || '';
      let selectedContractId = selectedContractIdValue;
      try {
        const parsedContractId = JSON.parse(selectedContractIdValue);
        if (Array.isArray(parsedContractId) && parsedContractId.length === 1) {
          selectedContractId = String(parsedContractId[0] || '');
        }
      } catch {
        selectedContractId = selectedContractIdValue;
      }
      if (selectedContractId) {
        menus = menus.map((menu) =>
          menu.key === 'representingProvider'
            ? { ...menu, name: `Contract ID : ${selectedContractId}` }
            : menu
        );
      }
    }
  }

  return menus;
};

/**
 * Get filtered secondary navigation menus specifically for PSMS pages
 * This function excludes Provider Performance and See what this provider sees items
 * @param options - Configuration options for filtering menus
 * @param options.isPSMSPage - If true, uses PSMS-specific menus without Provider Performance and See Provider View
 * @returns MenuItem[] - Filtered secondary menus appropriate for PSMS pages
 */
export const getPSMSFilteredSecondaryMenus = (options?: {
  isInternal?: boolean;
  disabledKeys?: string[];
  selectedView?: string;
  loginType?: string;
  modeType?: string;
  geoSegmentFlag?: boolean;
  geoCriteria?: string | null;
  isMultiTin?: boolean;
  externalProvidersCount?: boolean;
  isPSMSPage?: boolean;
}): MenuItem[] => {
  const {
    disabledKeys = [],
    loginType = Logins.Internal,
    modeType = Modes.General,
    geoSegmentFlag = false,
    geoCriteria = null,
    isPSMSPage = false,
  } = options || {};

  // If this is a PSMS page, use PSMS-specific secondary menus that exclude Provider Performance and See Provider View
  if (isPSMSPage) {
    // For External users or Simulation mode, return corporateTaxIdOwner instead of representingProvider
    const isExternalOrSimulation = loginType === Logins.External || modeType === Modes.Simulation;
    if (isExternalOrSimulation) {
      return [
        {
          key: 'corporateTaxIdOwner',
          name: '',
          path: '#',
          disabled: true,
          hasDropdown: false,
        },
        {
          key: 'taxIdNumber',
          name: '',
          path: '#',
          disabled: false,
          hasDropdown: true,
          children: [],
        },
      ];
    }

    // Clone the PSMS-specific menus to avoid mutating the original
    let menus = JSON.parse(JSON.stringify(PSMSSecondaryNavigationMenus)) as MenuItem[];

    // Special case: If GeoCriteria is not null (geo flow), hide View Tax ID Details and You are representing
    if (geoSegmentFlag && geoCriteria !== null && geoCriteria !== 'null') {
      menus = menus.filter((menu) => {
        // HIDE "View Tax ID Details" and "You are representing..." in geo flow
        if (menu.key === 'representingProvider' || menu.key === 'viewTaxId') {
          return false;
        }
        return true;
      });
    }

    const multiTinNavigation = sessionStorage.getItem('multiTinNavigation');
    const multiSelectedTins = sessionStorage.getItem('multiSelectedTins');
    const isMultiTinVal = multiTinNavigation === 'true';
    const tinsArray = multiSelectedTins ? multiSelectedTins.split(',') : [];
    const isExtendedMultiTinVal = isMultiTinVal && tinsArray.length > 1;
    const activeProviderMode = sessionStorage.getItem('simulationMode') === 'true';
    const isInternal = loginType === Logins.Internal;

    // Show "See what this provider sees" when: Internal + Not in simulation + (!isMultiTinVal || !isExtendedMultiTinVal) + Not in geo flow
    // This shows for: Single TIN, Multi-TIN with 1 TIN selected
    // This hides for: Extended Multi-TIN (multi-TIN with multiple TINs)
    const shouldShowSeeProviderView =
      isInternal &&
      !activeProviderMode &&
      (!isMultiTinVal || !isExtendedMultiTinVal) &&
      !geoSegmentFlag;

    // Hide View Tax ID Details and Change Providers for External users or Simulation mode
    if (!shouldShowSeeProviderView) {
      menus = menus.filter((menu) => menu.key !== 'seeProviderView');
    }

    // Note: Skip access pattern filtering for PSMS secondary menus since they're already page-specific
    // and should always show regardless of selectedView

    // Filter out disabled keys
    menus = menus
      .filter((menu) => {
        if (disabledKeys.includes(menu.key)) {
          return false;
        }
        return true;
      })
      .map((menu) => {
        // Filter children if they exist
        if (menu.children) {
          menu.children = menu.children.filter((child) => {
            if (disabledKeys.includes(child.key)) {
              return false;
            }
            return true;
          });
        }
        return menu;
      });
    return menus;
  }

  // For non-PSMS pages, use the regular filtered secondary menus
  return getFilteredSecondaryMenus(options);
};

export default NavigationMenus;
